import React, { useEffect, useRef, useState } from "react";
import { api, API_BASE, getAuthToken } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PaperPlaneTilt, Sparkle } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

const SUGGESTIONS = [
  "What foods should I limit if my potassium is high?",
  "Is it okay to take ibuprofen for a headache with CKD stage 3?",
  "How do I lower my ACR naturally?",
  "Explain what my eGFR of 52 means in simple terms.",
];

export default function Assistant() {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    (async () => {
      const res = await api.post("/chat/sessions");
      setSessionId(res.data.session_id);
    })();
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    if (!text.trim() || !sessionId || streaming) return;
    const userMsg = { role: "user", content: text };
    const assistantMsg = { role: "assistant", content: "" };
    setMessages((m) => [...m, userMsg, assistantMsg]);
    setInput("");
    setStreaming(true);
    try {
      const authHeader = getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {};
      const resp = await fetch(`${API_BASE}/chat/message`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data:")) continue;
          const payload = part.slice(5).trim();
          if (!payload) continue;
          try {
            const obj = JSON.parse(payload);
            if (obj.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + obj.delta };
                return copy;
              });
            } else if (obj.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], content: `(Error: ${obj.error})` };
                return copy;
              });
            }
          } catch (parseErr) { /* ignore malformed SSE chunk */ void parseErr; }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], content: "Sorry — I couldn't reach the assistant." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 min-h-[calc(100vh-180px)]" data-testid="assistant-grid">
      <div className="ros-card lg:col-span-1" data-testid="assistant-sidepanel">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl overflow-hidden border border-[#272E2B]">
            <img
              src="https://images.unsplash.com/photo-1594824476967-48c8b964273f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxjYXJpbmclMjBmZW1hbGUlMjBudXJzZSUyMHBvcnRyYWl0JTIwc3R1ZGlvfGVufDB8fHx8MTc4NTE2ODk0Nnww&ixlib=rb-4.1.0&q=85"
              alt="Renna" className="w-full h-full object-cover"
            />
          </div>
          <div>
            <div className="font-heading text-lg">Renna</div>
            <div className="text-xs text-[#A3ACA7]">AI nephrology assistant</div>
          </div>
        </div>
        <div className="text-xs text-[#A3ACA7] mt-6 leading-relaxed">
          Ask questions about diet, medications, symptoms, or lab interpretations. Renna provides supportive guidance — always confirm treatment decisions with your nephrologist.
        </div>

        <div className="ros-divider my-6" />

        <div className="ros-label">Try asking</div>
        <div className="mt-3 space-y-2">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              onClick={() => send(s)}
              disabled={streaming}
              data-testid={`suggestion-${s.slice(0, 15).replace(/\s+/g, "-").toLowerCase()}`}
              className="w-full text-left text-xs text-[#A3ACA7] hover:text-white ros-card-raised transition-colors"
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="ros-card lg:col-span-3 flex flex-col" data-testid="chat-panel">
        <div className="flex-1 overflow-y-auto pr-1 space-y-4 max-h-[65vh]">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center py-16">
              <Sparkle size={40} weight="duotone" color="#4ADE80" className="mb-4" />
              <div className="font-heading text-2xl">How can I help today?</div>
              <div className="text-sm text-[#A3ACA7] mt-2 max-w-md">
                I&apos;ll keep answers grounded in current renal care guidance. Pick a suggestion on the left or type below.
              </div>
            </div>
          )}
          <AnimatePresence>
            {messages.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`chat-msg-${i}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-3 rounded-2xl leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#4ADE80] text-black rounded-br-md"
                      : "bg-[#1C201E] text-white rounded-bl-md"
                  }`}
                  style={{ fontSize: 15 }}
                >
                  {m.content || (
                    <span className="text-[#A3ACA7]">
                      <span className="typing-dot">•</span>
                      <span className="typing-dot">•</span>
                      <span className="typing-dot">•</span>
                    </span>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          <div ref={endRef} />
        </div>

        <div className="ros-divider my-4" />

        <div className="flex items-center gap-3">
          <div className="flex-1 flex items-center gap-2 bg-[#1C201E] border border-[#272E2B] rounded-full px-4 py-2.5">
            <Input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send(input))}
              placeholder="Ask Renna anything about your kidney health…"
              className="bg-transparent border-none focus-visible:ring-0 focus-visible:ring-offset-0 h-auto p-0 text-white placeholder:text-[#A3ACA7]"
            />
          </div>
          <Button
            onClick={() => send(input)}
            disabled={!input.trim() || streaming}
            data-testid="chat-send-btn"
            className="rounded-full h-11 w-11 p-0 bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90"
          >
            <PaperPlaneTilt size={18} weight="fill" />
          </Button>
        </div>
      </div>
    </div>
  );
}
